import { ref } from 'valtio';

import type { MotionKeyPoseStatePayload } from '@cartwhl/common/api/postSceneObjectMotionKeyPoseEdit';
import putSceneObjectMotionKeyPoses from '@cartwhl/common/api/putSceneObjectMotionKeyPoses';
import type { SaveKeyPosesResponse } from '@cartwhl/common/api/putSceneObjectMotionKeyPoses';
import { BaseStore } from '@cartwhl/common/stores/BaseStore';
import { createStoreProvider } from '@cartwhl/common/stores/createStoreProvider';
import type { SceneTimelineKeyPoseFromAPI } from '@cartwhl/common/types';

import * as globalStores from '../../../stores/global';
import { isBuiltinFreeCharacterId } from '../../shared/freeCharacterMesh';
import {
  type MotionEditorStore,
  Context as MotionEditorStoreContext,
} from '../motionEditorStore';
import {
  resolveKeyPoseFkJointFromIkEffector,
  resolveKeyPoseIkEffectorFromFkJoint,
} from './ik/keyPoseIkConfig';
import {
  appendKeyPoseActionEntryInPlace,
  areKeyPoseActionSnapshotsEqual,
  cloneKeyPoseActionSnapshot,
  cloneKeyPoseIkTargetPositions,
  cloneKeyPoseRecord,
  createInitialKeyPoseActionStackState,
  createKeyPoseActionEntry,
} from './keyPoseActionStack';
import {
  type KeyPoseComparisonGhostScope,
  getKeyPoseComparisonGhostScopeKey,
  selectAvailableKeyPoseComparisonGhostHistoryEntries,
  selectKeyPoseComparisonGhostHistoryEntry,
  selectPlayableKeyPoseComparisonGhostHistoryEntries,
} from './keyPoseComparisonGhostUtils';
import {
  createInitialKeyPoseControlFrameSettings,
  normalizeKeyPoseControlFrameSettings,
  writeKeyPoseControlFrameSettingsToStorage,
} from './keyPoseControlFrameSettings';
import {
  type ApplyHistoryGenerationToSceneInput,
  type GetHistoryGenerationBvhURLInput,
  type KeyPoseGenerationActionDeps,
  type KeyPoseGenerationActionResult,
  type LoadSceneObjectMotionEditHistoryInput,
  type RecoverSceneObjectMotionKeyPoseGenerationInput,
  type RegenerateSceneObjectMotionKeyPosesInput,
  type RegenerateSceneObjectMotionKeyPosesResult,
  applyHistoryGenerationToScene,
  clearGenerationHistory,
  getHistoryGenerationBvhURL,
  loadSceneObjectMotionEditHistory,
  recoverSceneObjectMotionKeyPoseGeneration,
  regenerateSceneObjectMotionKeyPoses,
  syncGenerationHistoryForCurrentScene,
} from './keyPoseGenerationActions';
import {
  DEFAULT_KEY_POSE_GHOST_MATERIAL_VARIANT,
  isKeyPoseGhostMaterialVariant,
} from './keyPoseGhostMaterialVariants';
import { DEFAULT_KEY_POSE_ONION_SKIN_OPTIONS } from './keyPoseGhostOnionSkin';
import {
  type KeyPoseIntervalAugmentationContext,
  augmentKeyPosesWithIntervalKeys,
} from './keyPoseIntervalAugmentation';
import {
  DEFAULT_INTERVAL_AUGMENTATION_SETTINGS,
  normalizeIntervalAugmentationSettings,
} from './keyPoseIntervalAugmentationSettings';
import { resolveKeyPoseMotionContext } from './keyPoseMotionUtils';
import { createRecoveredKeyPoseInputsFromApiKeyPoses } from './keyPoseRecovery';
import {
  createIntervalKeyPoseId,
  hasKeyPoseBoneSelection,
  isIntervalKeyPoseRecord,
  resolveKeyPoseStamp,
  selectIntervalStampApiPayloadForReference,
  selectKeyPoseApiPayloadForReference,
  selectUserKeyPoseApiPayloadForReference,
  shouldReplaceIntervalStampWithUserKeyPose,
} from './keyPoseSelectors';
import {
  cloneKeyPoseSkeletonSnapshot,
  cloneKeyPoseSourcePose,
  getErrorMessage,
  getKeyPosePersistenceKey,
} from './keyPoseStoreUtils';
import { createKeyPoseTimingLogger } from './keyPoseTiming';
import type {
  KeyPoseActionSnapshot,
  KeyPoseControlFrameSettings,
  KeyPoseEditingMode,
  KeyPoseEditingPreviewRecord,
  KeyPoseFkJointId,
  KeyPoseGenerationHistoryEntry,
  KeyPoseGhostMaterialVariant,
  KeyPoseHoverPreviewState,
  KeyPoseId,
  KeyPoseIkEffectorId,
  KeyPoseIkTargetPositions,
  KeyPoseIntervalAugmentationSettings,
  KeyPoseRecord,
  KeyPoseSkeletonSnapshot,
  KeyPoseSourcePosePayload,
  KeyPoseState,
  ReferenceKeyPoseState,
} from './types';

const AUTH_ERROR_MESSAGE = 'Failed to authenticate. Please try again.';

type Deps = {
  global: typeof globalStores;
  parentStore: MotionEditorStore;
};

export type SaveSceneObjectMotionKeyPosesInput = {
  sceneID: string;
  referenceName: string;
  timelineIndex: number;
  keyPoses: MotionKeyPoseStatePayload;
  intervalAugmentation?: KeyPoseIntervalAugmentationContext;
  signal?: AbortSignal;
};

export type SaveCurrentSceneObjectMotionKeyPosesInput = Omit<
  SaveSceneObjectMotionKeyPosesInput,
  'keyPoses'
>;

export type { KeyPoseIntervalAugmentationContext };

export type AddKeyPoseInput = {
  frame: number;
  referenceName: string;
  skeletonSnapshot: KeyPoseSkeletonSnapshot;
  sourcePose: KeyPoseSourcePosePayload;
  stamp?: KeyPoseRecord['stamp'];
};

export type CopyKeyPoseSnapshotInput = AddKeyPoseInput;

export type PasteKeyPoseInput = {
  frame: number;
  referenceName: string;
};

export type HideKeyPosesForReferenceInput = {
  referenceName: string;
  excludeKeyPoseId?: KeyPoseId | null;
};

export type UnsupportedCharacterKeyPosePromptInput = {
  keyPoseId: KeyPoseId;
  referenceName: string;
};

export type ShowKeyPosesForReferenceInput = {
  referenceName: string;
};

export type RemoveKeyPosesForReferenceInput = {
  referenceName: string;
};

export type ReplaceRecoveredSceneKeyPoseInput = {
  id?: string;
  frame: number;
  skeletonSnapshot: KeyPoseSkeletonSnapshot;
  sourcePose: KeyPoseSourcePosePayload;
};

export type ReplaceRecoveredSceneKeyPosesInput = {
  referenceName: string;
  keyPoses: ReplaceRecoveredSceneKeyPoseInput[];
};

export type UpdateKeyPoseInput = {
  keyPoseId: KeyPoseId;
  skeletonSnapshot: KeyPoseSkeletonSnapshot;
  sourcePose: KeyPoseSourcePosePayload;
};

export type SetKeyPoseEditingPreviewInput = {
  keyPoseId: KeyPoseId;
  clone?: boolean;
} & KeyPoseEditingPreviewRecord;

export type SetKeyPoseIkTargetPositionInput = {
  keyPoseId: KeyPoseId;
  effectorId: KeyPoseIkEffectorId;
  position: readonly [number, number, number];
};

export type TranslateKeyPoseIkTargetPositionsInput = {
  keyPoseId: KeyPoseId;
  delta: readonly [number, number, number];
};

export type SelectComparisonGhostHistoryEntryInput = {
  entry: KeyPoseGenerationHistoryEntry;
  scope?: KeyPoseComparisonGhostScope | null;
};

export type CreateRecoveredHistoryKeyPoseInputs = (
  keyPoses: SceneTimelineKeyPoseFromAPI[],
) => ReplaceRecoveredSceneKeyPoseInput[];

export type ApplyHistoryGenerationToSceneStoreInput = Omit<
  ApplyHistoryGenerationToSceneInput,
  'apiKey'
> & {
  createRecoveredKeyPoses?: CreateRecoveredHistoryKeyPoseInputs;
};

type KeyPoseWriteResult = {
  keyPoseId: KeyPoseId;
  created: boolean;
  didWrite: boolean;
  frame: number;
  referenceName: string;
};

type KeyPoseRemoveResult = {
  keyPoseId: KeyPoseId;
  frame: number;
  referenceName: string;
};

function createInitialKeyPoseState(): KeyPoseState {
  return {
    actionStack: createInitialKeyPoseActionStackState(),
    activeReferenceName: null,
    clipboard: null,
    comparisonGhost: createInitialKeyPoseComparisonGhostState(),
    generation: createInitialKeyPoseGenerationState(),
    generationHistory: [],
    generationHistoryLoading: false,
    ghostOptions: createInitialKeyPoseGhostOptionsState(),
    hasApiKey: false,
    unsupportedCharacterKeyPosePrompt: null,
    hoverPreview: null,
    editing: createInitialKeyPoseEditingState(),
    referencesByName: {},
    keyPosesById: {},
    persistenceByMotionKey: {},
    intervalAugmentationSettings: {
      ...DEFAULT_INTERVAL_AUGMENTATION_SETTINGS,
    },
    controlFrameSettings: createInitialKeyPoseControlFrameSettings(),
  };
}

function createInitialKeyPoseComparisonGhostState() {
  return {
    appliedHistoryJobID: null,
    disabled: true,
    scopeKey: null,
    selectedHistoryJobID: null,
    loadingHistoryJobID: null,
    error: null,
  } satisfies KeyPoseState['comparisonGhost'];
}

function createInitialKeyPoseGhostOptionsState() {
  return {
    materialVariant: DEFAULT_KEY_POSE_GHOST_MATERIAL_VARIANT,
    onionSkin: { ...DEFAULT_KEY_POSE_ONION_SKIN_OPTIONS },
    showOnlyCurrentActor: true,
    useActorColors: false,
  } satisfies KeyPoseState['ghostOptions'];
}

function createInitialKeyPoseGenerationState() {
  return {
    status: 'idle',
    phase: null,
    activeRunId: null,
    error: null,
  } satisfies KeyPoseState['generation'];
}

function createInitialKeyPoseEditingState() {
  return {
    mode: 'fk',
    selectedFkJointId: null,
    hoveredFkJointId: null,
    selectedIkEffectorId: null,
    hoveredIkEffectorId: null,
    lockFeet: false,
    dragging: false,
    draggingKeyPoseId: null,
    previewByKeyPoseId: {},
    ikTargetPositionsByKeyPoseId: {},
  } satisfies KeyPoseState['editing'];
}

class KeyPoseStore extends BaseStore<KeyPoseState, Deps> {
  private sceneID: string | null = null;
  private generationHistoryLoadAbortController: AbortController | null = null;
  private generationHistoryLoadRequestID = 0;

  constructor(deps: Deps) {
    super(createInitialKeyPoseState(), deps);
  }

  init() {
    this.syncSceneScope();
    this.syncApiKeyAvailability();
    this.syncActiveActor();
    syncGenerationHistoryForCurrentScene(this.getGenerationActionDeps());
    this.subscribeTo(
      this.deps.global.userStore.state,
      () => this.syncApiKeyAvailability(),
      { keys: 'apiKey' },
    );
    this.subscribeTo(
      this.deps.parentStore.state,
      () => this.syncActiveActor(),
      { keys: 'playback' },
    );
    this.subscribeTo(
      this.deps.parentStore.state,
      () => {
        this.syncSceneScope();
        syncGenerationHistoryForCurrentScene(this.getGenerationActionDeps());
      },
      { keys: 'scene' },
    );
  }

  destroy() {
    this.cancelGenerationHistoryLoad();
    super.destroy();
  }

  reset() {
    this.cancelGenerationHistoryLoad();
    this.replaceState(createInitialKeyPoseState());
    this.sceneID = this.deps.parentStore.state.scene.data?.sceneID ?? null;
    this.syncApiKeyAvailability();
    this.syncActiveActor();
    syncGenerationHistoryForCurrentScene(this.getGenerationActionDeps());
  }

  setShowOnlyCurrentActorGhosts(showOnlyCurrentActor: boolean) {
    this.state.ghostOptions.showOnlyCurrentActor = showOnlyCurrentActor;
  }

  setUseActorColorGhosts(useActorColors: boolean) {
    this.state.ghostOptions.useActorColors = useActorColors;
  }

  setKeyPoseGhostMaterialVariant(variant: KeyPoseGhostMaterialVariant) {
    if (!isKeyPoseGhostMaterialVariant(variant)) {
      return;
    }

    this.state.ghostOptions.materialVariant = variant;
  }

  setKeyPoseOnionSkinGhostsEnabled(enabled: boolean) {
    this.state.ghostOptions.onionSkin.enabled = enabled;
  }

  setUnsupportedCharacterKeyPosePrompt({
    keyPoseId,
    referenceName,
  }: UnsupportedCharacterKeyPosePromptInput) {
    const keyPose = this.state.keyPosesById[keyPoseId];
    const normalizedReferenceName = referenceName.trim();
    if (!keyPose || !normalizedReferenceName) {
      return false;
    }

    this.state.unsupportedCharacterKeyPosePrompt = {
      keyPoseId,
      referenceName: normalizedReferenceName,
    };
    return true;
  }

  clearUnsupportedCharacterKeyPosePrompt(keyPoseId?: KeyPoseId) {
    if (
      keyPoseId &&
      this.state.unsupportedCharacterKeyPosePrompt?.keyPoseId !== keyPoseId
    ) {
      return;
    }

    this.state.unsupportedCharacterKeyPosePrompt = null;
  }

  addKeyPose({
    frame,
    referenceName,
    skeletonSnapshot,
    sourcePose,
    stamp = 'user',
  }: AddKeyPoseInput): KeyPoseWriteResult | null {
    return this.writeKeyPose(
      { frame, referenceName, skeletonSnapshot, sourcePose, stamp },
      false,
    );
  }

  setIntervalAugmentationSettings(
    settings: Partial<KeyPoseIntervalAugmentationSettings>,
  ) {
    this.state.intervalAugmentationSettings =
      normalizeIntervalAugmentationSettings(
        settings,
        this.state.intervalAugmentationSettings,
      );
  }

  setControlFrameSettings(settings: Partial<KeyPoseControlFrameSettings>) {
    const previousSettings = this.state.controlFrameSettings;
    this.state.controlFrameSettings = normalizeKeyPoseControlFrameSettings(
      settings,
      this.state.controlFrameSettings,
    );
    writeKeyPoseControlFrameSettingsToStorage(this.state.controlFrameSettings);

    if (
      previousSettings.autoGenerateControlFrames &&
      !this.state.controlFrameSettings.autoGenerateControlFrames
    ) {
      this.removeAllIntervalKeyPoses();
      return;
    }

    this.syncControlFrameGhostVisibility();
  }

  syncControlFrameGhostVisibility() {
    this.syncAllIntervalKeyPoseGhostsInScene();
  }

  copyKeyPose(keyPoseId: KeyPoseId) {
    const keyPose = this.state.keyPosesById[keyPoseId];
    if (!keyPose) {
      return false;
    }

    this.state.clipboard = {
      sourceFrame: keyPose.frame,
      sourceKeyPoseId: keyPose.id,
      sourceReferenceName:
        this.findReferenceNameForKeyPose(keyPoseId) ?? undefined,
      skeletonSnapshot: cloneKeyPoseSkeletonSnapshot(
        keyPose.skeletonSnapshot,
        keyPose.frame,
      ),
      sourcePose: cloneKeyPoseSourcePose(keyPose.sourcePose, keyPose.frame),
    };

    return true;
  }

  copyKeyPoseSnapshot({
    frame,
    referenceName,
    skeletonSnapshot,
    sourcePose,
  }: CopyKeyPoseSnapshotInput) {
    const normalizedReferenceName = referenceName.trim();
    if (!normalizedReferenceName) {
      return false;
    }

    const normalizedFrame = Math.max(Math.round(frame), 0);
    this.state.clipboard = {
      sourceFrame: normalizedFrame,
      sourceReferenceName: normalizedReferenceName,
      skeletonSnapshot: cloneKeyPoseSkeletonSnapshot(
        skeletonSnapshot,
        normalizedFrame,
      ),
      sourcePose: cloneKeyPoseSourcePose(sourcePose, normalizedFrame),
    };
    this.state.activeReferenceName = normalizedReferenceName;

    return true;
  }

  pasteKeyPose({
    frame,
    referenceName,
  }: PasteKeyPoseInput): KeyPoseWriteResult | null {
    if (!this.state.clipboard) {
      return null;
    }

    return this.writeKeyPose(
      {
        frame,
        referenceName,
        skeletonSnapshot: this.state.clipboard.skeletonSnapshot,
        sourcePose: this.state.clipboard.sourcePose,
      },
      true,
    );
  }

  removeKeyPose(keyPoseId: KeyPoseId): KeyPoseRemoveResult | null {
    const keyPose = this.state.keyPosesById[keyPoseId];
    if (!keyPose) {
      return null;
    }

    for (const referenceState of Object.values(this.state.referencesByName)) {
      const keyPoseIndex = referenceState.keyPoseIds.indexOf(keyPoseId);
      if (keyPoseIndex === -1) {
        continue;
      }

      referenceState.keyPoseIds.splice(keyPoseIndex, 1);
      delete this.state.keyPosesById[keyPoseId];
      this.clearDeletedKeyPoseEditingState(keyPoseId);
      this.clearUnsupportedCharacterKeyPosePrompt(keyPoseId);
      return {
        keyPoseId,
        frame: keyPose.frame,
        referenceName: referenceState.referenceName,
      };
    }

    delete this.state.keyPosesById[keyPoseId];
    this.clearDeletedKeyPoseEditingState(keyPoseId);
    this.clearUnsupportedCharacterKeyPosePrompt(keyPoseId);
    return null;
  }

  removeKeyPosesForReference({
    referenceName,
  }: RemoveKeyPosesForReferenceInput): {
    referenceName: string;
    count: number;
  } | null {
    const normalizedReferenceName = referenceName.trim();
    const referenceState = this.state.referencesByName[normalizedReferenceName];
    if (!normalizedReferenceName || !referenceState) {
      return null;
    }

    const keyPoseIds = [...referenceState.keyPoseIds];
    if (keyPoseIds.length === 0) {
      return null;
    }

    for (const keyPoseId of keyPoseIds) {
      delete this.state.keyPosesById[keyPoseId];
      this.clearDeletedKeyPoseEditingState(keyPoseId);
      this.clearUnsupportedCharacterKeyPosePrompt(keyPoseId);
    }
    referenceState.keyPoseIds = [];
    this.state.actionStack = createInitialKeyPoseActionStackState();

    if (this.state.hoverPreview?.referenceName === normalizedReferenceName) {
      this.clearHoverPreview();
    }

    return {
      referenceName: normalizedReferenceName,
      count: keyPoseIds.length,
    };
  }

  setHoverPreview({ frame, referenceName }: KeyPoseHoverPreviewState) {
    const normalizedReferenceName = referenceName.trim();
    if (!normalizedReferenceName) {
      this.clearHoverPreview();
      return;
    }

    this.state.hoverPreview = {
      frame: Math.max(Math.round(frame), 0),
      referenceName: normalizedReferenceName,
    };
  }

  clearHoverPreview() {
    this.state.hoverPreview = null;
  }

  toggleKeyPoseHidden(keyPoseId: KeyPoseId) {
    const keyPose = this.state.keyPosesById[keyPoseId];
    if (!keyPose) {
      return false;
    }

    keyPose.hidden = !keyPose.hidden;
    if (keyPose.hidden) {
      delete this.state.editing.previewByKeyPoseId[keyPoseId];
    }

    return true;
  }

  showKeyPose(keyPoseId: KeyPoseId) {
    const keyPose = this.state.keyPosesById[keyPoseId];
    if (!keyPose) {
      return false;
    }

    delete keyPose.hidden;

    return true;
  }

  upgradeIntervalKeyPoseToUser(keyPoseId: KeyPoseId): KeyPoseId | null {
    const keyPose = this.state.keyPosesById[keyPoseId];
    if (!keyPose) {
      return null;
    }

    if (!isIntervalKeyPoseRecord(keyPose)) {
      return keyPoseId;
    }

    const referenceName = this.findReferenceNameForKeyPose(keyPoseId);
    const referenceState = referenceName
      ? this.state.referencesByName[referenceName]
      : undefined;
    if (!referenceName || !referenceState) {
      return null;
    }

    const upgradedKeyPoseId = crypto.randomUUID();
    const upgradedKeyPose = cloneKeyPoseRecord(keyPose);
    upgradedKeyPose.id = upgradedKeyPoseId;
    upgradedKeyPose.stamp = 'user';
    delete upgradedKeyPose.hidden;

    const ikTargets =
      this.state.editing.ikTargetPositionsByKeyPoseId[keyPoseId];
    const preview = this.state.editing.previewByKeyPoseId[keyPoseId];

    this.removeKeyPoseFromReference(keyPoseId, referenceState);
    this.state.keyPosesById[upgradedKeyPoseId] = upgradedKeyPose;
    referenceState.keyPoseIds.push(upgradedKeyPoseId);
    this.sortReferenceKeyPoseIds(referenceState);

    if (ikTargets) {
      this.state.editing.ikTargetPositionsByKeyPoseId[upgradedKeyPoseId] =
        ikTargets;
    }

    if (preview) {
      this.state.editing.previewByKeyPoseId[upgradedKeyPoseId] = preview;
    }

    console.log(
      `[keyPoseIntervalAugmentation] interval key pose upgraded to user at ${keyPose.frame} frame`,
    );

    return upgradedKeyPoseId;
  }

  hideKeyPosesForReference({
    referenceName,
    excludeKeyPoseId,
  }: HideKeyPosesForReferenceInput) {
    const normalizedReferenceName = referenceName.trim();
    const referenceState = this.state.referencesByName[normalizedReferenceName];
    if (!normalizedReferenceName || !referenceState) {
      return 0;
    }

    let hiddenCount = 0;
    for (const keyPoseId of referenceState.keyPoseIds) {
      if (keyPoseId === excludeKeyPoseId) {
        continue;
      }

      const keyPose = this.state.keyPosesById[keyPoseId];
      if (!keyPose || keyPose.hidden || isIntervalKeyPoseRecord(keyPose)) {
        continue;
      }

      keyPose.hidden = true;
      delete this.state.editing.previewByKeyPoseId[keyPoseId];
      hiddenCount += 1;
    }

    return hiddenCount;
  }

  showKeyPosesForReference({ referenceName }: ShowKeyPosesForReferenceInput) {
    const normalizedReferenceName = referenceName.trim();
    const referenceState = this.state.referencesByName[normalizedReferenceName];
    if (!normalizedReferenceName || !referenceState) {
      return 0;
    }

    let shownCount = 0;
    for (const keyPoseId of referenceState.keyPoseIds) {
      const keyPose = this.state.keyPosesById[keyPoseId];
      if (!keyPose?.hidden || isIntervalKeyPoseRecord(keyPose)) {
        continue;
      }

      delete keyPose.hidden;
      shownCount += 1;
    }

    return shownCount;
  }

  private syncIntervalKeyPoseGhostsForReference(referenceName: string) {
    const referenceState = this.state.referencesByName[referenceName];
    if (!referenceState) {
      return;
    }

    const showAllIntervalGhosts =
      this.state.controlFrameSettings.showControlFrameGhosts;

    for (const keyPoseId of referenceState.keyPoseIds) {
      const keyPose = this.state.keyPosesById[keyPoseId];
      if (!keyPose || !isIntervalKeyPoseRecord(keyPose)) {
        continue;
      }

      if (showAllIntervalGhosts) {
        delete keyPose.hidden;
        continue;
      }

      keyPose.hidden = true;
      delete this.state.editing.previewByKeyPoseId[keyPoseId];
    }
  }

  private syncAllIntervalKeyPoseGhostsInScene() {
    for (const referenceName of Object.keys(this.state.referencesByName)) {
      this.syncIntervalKeyPoseGhostsForReference(referenceName);
    }
  }

  private removeAllIntervalKeyPoses(): number {
    const intervalKeyPoseIds = Object.values(this.state.keyPosesById)
      .filter(
        (keyPose): keyPose is KeyPoseRecord =>
          keyPose !== undefined && isIntervalKeyPoseRecord(keyPose),
      )
      .map((keyPose) => keyPose.id);

    if (intervalKeyPoseIds.length === 0) {
      return 0;
    }

    const hadIntervalEditing =
      Boolean(this.state.editing.draggingKeyPoseId) ||
      hasKeyPoseBoneSelection(this.state.editing);

    for (const keyPoseId of intervalKeyPoseIds) {
      this.removeKeyPose(keyPoseId);
    }

    if (hadIntervalEditing) {
      this.clearKeyPoseEditingSelection();
    }

    return intervalKeyPoseIds.length;
  }

  setKeyPoseEditingMode(mode: KeyPoseEditingMode) {
    const { editing } = this.state;
    if (editing.mode === mode) return;

    if (mode === 'fk') {
      editing.selectedFkJointId = resolveKeyPoseFkJointFromIkEffector(
        editing.selectedIkEffectorId,
        editing.selectedFkJointId,
      );
      editing.hoveredIkEffectorId = null;
      editing.mode = mode;
      return;
    }

    editing.selectedIkEffectorId =
      resolveKeyPoseIkEffectorFromFkJoint(editing.selectedFkJointId) ??
      editing.selectedIkEffectorId;
    editing.hoveredFkJointId = null;
    editing.mode = mode;
  }

  setKeyPoseFkSelectedJoint(jointId: KeyPoseFkJointId | null) {
    this.state.editing.selectedFkJointId = jointId;
    if (jointId) {
      this.state.editing.mode = 'fk';
      this.state.editing.selectedIkEffectorId =
        resolveKeyPoseIkEffectorFromFkJoint(jointId);
      this.state.editing.hoveredIkEffectorId = null;
    }
  }

  setKeyPoseFkHoveredJoint(jointId: KeyPoseFkJointId | null) {
    if (this.state.editing.hoveredFkJointId === jointId) return;

    this.state.editing.hoveredFkJointId = jointId;
  }

  setKeyPoseIkSelectedEffector(effectorId: KeyPoseIkEffectorId | null) {
    this.state.editing.selectedIkEffectorId = effectorId;
    if (effectorId) {
      this.state.editing.mode = 'ik';
      this.state.editing.selectedFkJointId =
        resolveKeyPoseFkJointFromIkEffector(
          effectorId,
          this.state.editing.selectedFkJointId,
        );
      this.state.editing.hoveredFkJointId = null;
    }
  }

  setKeyPoseIkHoveredEffector(effectorId: KeyPoseIkEffectorId | null) {
    if (this.state.editing.hoveredIkEffectorId === effectorId) return;

    this.state.editing.hoveredIkEffectorId = effectorId;
  }

  clearKeyPoseEditingSelection() {
    const {
      hoveredFkJointId,
      hoveredIkEffectorId,
      selectedFkJointId,
      selectedIkEffectorId,
    } = this.state.editing;
    const hadSelection = Boolean(
      hoveredFkJointId ||
        hoveredIkEffectorId ||
        selectedFkJointId ||
        selectedIkEffectorId,
    );

    this.state.editing.selectedFkJointId = null;
    this.state.editing.hoveredFkJointId = null;
    this.state.editing.selectedIkEffectorId = null;
    this.state.editing.hoveredIkEffectorId = null;
    this.clearKeyPoseEditingPreview();

    return hadSelection;
  }

  setKeyPoseLockFeet(lockFeet: boolean) {
    this.state.editing.lockFeet = lockFeet;
  }

  setKeyPoseEditingDragging(
    dragging: boolean,
    keyPoseId: KeyPoseId | null = null,
  ) {
    this.state.editing.dragging = dragging;
    this.state.editing.draggingKeyPoseId = dragging ? keyPoseId : null;
  }

  setKeyPoseEditingPreview({
    keyPoseId,
    skeletonSnapshot,
    sourcePose,
    clone = true,
  }: SetKeyPoseEditingPreviewInput) {
    const keyPose = this.state.keyPosesById[keyPoseId];
    if (!keyPose) {
      return false;
    }

    this.state.editing.previewByKeyPoseId[keyPoseId] = clone
      ? {
          skeletonSnapshot: cloneKeyPoseSkeletonSnapshot(
            skeletonSnapshot,
            keyPose.frame,
          ),
          sourcePose: cloneKeyPoseSourcePose(sourcePose, keyPose.frame),
        }
      : {
          skeletonSnapshot,
          sourcePose,
        };

    return true;
  }

  clearKeyPoseEditingPreview(keyPoseId?: KeyPoseId) {
    if (keyPoseId) {
      delete this.state.editing.previewByKeyPoseId[keyPoseId];
      return;
    }

    this.state.editing.previewByKeyPoseId = {};
  }

  updateKeyPose({
    keyPoseId,
    skeletonSnapshot,
    sourcePose,
  }: UpdateKeyPoseInput): KeyPoseWriteResult | null {
    const timing = createKeyPoseTimingLogger('updateKeyPose', {
      keyPoseId,
      sourcePoseJointCount: sourcePose.localJointRot.length,
    });

    const upgradedKeyPoseId = this.upgradeIntervalKeyPoseToUser(keyPoseId);
    const resolvedKeyPoseId = upgradedKeyPoseId ?? keyPoseId;
    timing.mark('resolved key pose id', {
      resolvedKeyPoseId,
      upgraded: Boolean(upgradedKeyPoseId),
    });

    const keyPose = this.state.keyPosesById[resolvedKeyPoseId];
    timing.mark('looked up key pose', { hasKeyPose: Boolean(keyPose) });
    if (!keyPose) {
      timing.end('aborted missing key pose');
      return null;
    }

    const referenceName = this.findReferenceNameForKeyPose(resolvedKeyPoseId);
    timing.mark('found reference name', {
      hasReferenceName: Boolean(referenceName),
    });
    if (!referenceName) {
      timing.end('aborted missing reference name');
      return null;
    }

    const frame = keyPose.frame;
    const nextSkeletonSnapshot = cloneKeyPoseSkeletonSnapshot(
      skeletonSnapshot,
      frame,
    );
    timing.mark('cloned skeleton snapshot');
    keyPose.skeletonSnapshot = ref(nextSkeletonSnapshot);
    timing.mark('assigned skeleton snapshot');

    const nextSourcePose = cloneKeyPoseSourcePose(sourcePose, frame);
    timing.mark('cloned source pose');
    keyPose.sourcePose = nextSourcePose;
    timing.mark('assigned source pose');

    this.state.activeReferenceName = referenceName;
    timing.end('set active reference');

    return {
      keyPoseId: resolvedKeyPoseId,
      created: false,
      didWrite: true,
      frame,
      referenceName,
    };
  }

  captureKeyPoseActionSnapshot(keyPoseId: KeyPoseId): KeyPoseActionSnapshot {
    const keyPose = this.state.keyPosesById[keyPoseId];

    return {
      keyPose: keyPose ? cloneKeyPoseRecord(keyPose) : null,
      ikTargetPositions: cloneKeyPoseIkTargetPositions(
        this.state.editing.ikTargetPositionsByKeyPoseId[keyPoseId],
      ),
    };
  }

  recordKeyPoseEditAction({
    keyPoseId,
    referenceName,
    label,
    before,
  }: {
    keyPoseId: KeyPoseId;
    referenceName: string;
    label: string;
    before: KeyPoseActionSnapshot;
  }) {
    const timing = createKeyPoseTimingLogger('recordKeyPoseEditAction', {
      keyPoseId,
      referenceName,
      label,
      actionStackIndex: this.state.actionStack.index,
      actionStackLength: this.state.actionStack.entries.length,
      beforeHasKeyPose: Boolean(before.keyPose),
      beforeTargetCount: countKeyPoseIkTargetPositions(
        before.ikTargetPositions,
      ),
    });

    const after = this.captureKeyPoseActionSnapshot(keyPoseId);
    timing.mark('captured after snapshot', {
      afterHasKeyPose: Boolean(after.keyPose),
      afterTargetCount: countKeyPoseIkTargetPositions(after.ikTargetPositions),
    });

    const snapshotsEqual = areKeyPoseActionSnapshotsEqual(before, after);
    timing.mark('compared snapshots', { snapshotsEqual });
    if (snapshotsEqual) {
      timing.end('skipped unchanged action');
      return false;
    }

    const entry = createKeyPoseActionEntry({
      kind: 'edit',
      label,
      referenceName,
      keyPoseId,
      before,
      after,
    });
    timing.mark('created action entry');

    const actionStack = appendKeyPoseActionEntryInPlace(
      this.state.actionStack,
      entry,
    );
    timing.end('mutated action stack', {
      actionStackIndex: actionStack.index,
      actionStackLength: actionStack.entries.length,
    });

    return true;
  }

  canUndoKeyPoseAction() {
    return this.state.actionStack.index >= 0;
  }

  canRedoKeyPoseAction() {
    return (
      this.state.actionStack.index >= -1 &&
      this.state.actionStack.index < this.state.actionStack.entries.length - 1
    );
  }

  undoKeyPoseAction(): { referenceName: string } | null {
    if (!this.canUndoKeyPoseAction()) {
      return null;
    }

    const entry = this.state.actionStack.entries[this.state.actionStack.index];
    if (!entry) {
      return null;
    }

    this.applyKeyPoseActionSnapshot({
      referenceName: entry.referenceName,
      keyPoseId: entry.keyPoseId,
      snapshot: entry.before,
    });
    this.state.actionStack.index -= 1;

    return { referenceName: entry.referenceName };
  }

  redoKeyPoseAction(): { referenceName: string } | null {
    if (!this.canRedoKeyPoseAction()) {
      return null;
    }

    const nextIndex = this.state.actionStack.index + 1;
    const entry = this.state.actionStack.entries[nextIndex];
    if (!entry) {
      return null;
    }

    this.applyKeyPoseActionSnapshot({
      referenceName: entry.referenceName,
      keyPoseId: entry.keyPoseId,
      snapshot: entry.after,
    });
    this.state.actionStack.index = nextIndex;

    return { referenceName: entry.referenceName };
  }

  setKeyPoseIkTargetPosition({
    keyPoseId,
    effectorId,
    position,
  }: SetKeyPoseIkTargetPositionInput) {
    if (!this.state.keyPosesById[keyPoseId]) {
      return false;
    }

    const targetPositions = this.ensureKeyPoseIkTargetPositions(keyPoseId);
    targetPositions[effectorId] = [position[0], position[1], position[2]];
    return true;
  }

  translateKeyPoseIkTargetPositions({
    keyPoseId,
    delta,
  }: TranslateKeyPoseIkTargetPositionsInput) {
    const targetPositions =
      this.state.editing.ikTargetPositionsByKeyPoseId[keyPoseId];
    if (!targetPositions) {
      return false;
    }

    for (const effectorId of Object.keys(
      targetPositions,
    ) as KeyPoseIkEffectorId[]) {
      const position = targetPositions[effectorId];
      if (!position) {
        continue;
      }

      targetPositions[effectorId] = [
        position[0] + delta[0],
        position[1] + delta[1],
        position[2] + delta[2],
      ];
    }

    return true;
  }

  replaceRecoveredSceneKeyPoses({
    referenceName,
    keyPoses,
  }: ReplaceRecoveredSceneKeyPosesInput) {
    const normalizedReferenceName = referenceName.trim();
    if (!normalizedReferenceName) {
      return false;
    }

    const referenceState = this.ensureReferenceState(normalizedReferenceName);
    const hiddenKeyPoseIds = new Set<KeyPoseId>();
    const hiddenKeyPoseFrames = new Set<number>();
    const intervalStampFrames = new Set<number>();
    for (const keyPoseId of referenceState.keyPoseIds) {
      const keyPose = this.state.keyPosesById[keyPoseId];
      if (!keyPose) {
        continue;
      }

      if (isIntervalKeyPoseRecord(keyPose)) {
        intervalStampFrames.add(keyPose.frame);
      }

      if (keyPose.hidden) {
        hiddenKeyPoseIds.add(keyPoseId);
        hiddenKeyPoseFrames.add(keyPose.frame);
      }
    }

    for (const keyPoseId of referenceState.keyPoseIds) {
      delete this.state.keyPosesById[keyPoseId];
    }

    referenceState.keyPoseIds = [];
    keyPoses.forEach((keyPose, index) => {
      const normalizedKeyPose = this.createRecoveredKeyPoseRecord(
        normalizedReferenceName,
        keyPose,
        index,
      );
      if (intervalStampFrames.has(normalizedKeyPose.frame)) {
        normalizedKeyPose.stamp = 'interval';
        normalizedKeyPose.hidden = true;
      } else if (
        hiddenKeyPoseIds.has(normalizedKeyPose.id) ||
        hiddenKeyPoseFrames.has(normalizedKeyPose.frame)
      ) {
        normalizedKeyPose.hidden = true;
      }
      this.state.keyPosesById[normalizedKeyPose.id] = normalizedKeyPose;
      referenceState.keyPoseIds.push(normalizedKeyPose.id);
    });
    referenceState.keyPoseIds.sort(
      (a, b) =>
        this.state.keyPosesById[a].frame - this.state.keyPosesById[b].frame,
    );

    if (this.state.hoverPreview?.referenceName === normalizedReferenceName) {
      this.clearHoverPreview();
    }
    for (const keyPoseId of Object.keys(
      this.state.editing.previewByKeyPoseId,
    )) {
      if (!this.state.keyPosesById[keyPoseId]) {
        delete this.state.editing.previewByKeyPoseId[keyPoseId];
      }
    }
    for (const keyPoseId of Object.keys(
      this.state.editing.ikTargetPositionsByKeyPoseId,
    )) {
      if (!this.state.keyPosesById[keyPoseId]) {
        delete this.state.editing.ikTargetPositionsByKeyPoseId[keyPoseId];
      }
    }
    this.state.actionStack = createInitialKeyPoseActionStackState();
    this.syncIntervalKeyPoseGhostsForReference(normalizedReferenceName);

    return true;
  }

  async saveSceneObjectMotionKeyPoses({
    sceneID,
    referenceName,
    timelineIndex,
    keyPoses,
    intervalAugmentation,
    signal,
  }: SaveSceneObjectMotionKeyPosesInput): Promise<boolean> {
    const persistenceKey = getKeyPosePersistenceKey(
      sceneID,
      referenceName,
      timelineIndex,
    );
    this.state.persistenceByMotionKey[persistenceKey] = {
      status: 'saving',
      error: null,
    };
    const apiKey = this.apiKey;
    if (!apiKey) {
      this.state.persistenceByMotionKey[persistenceKey] = {
        status: 'failed',
        error: AUTH_ERROR_MESSAGE,
      };
      return false;
    }

    const keyPosesToSave = intervalAugmentation
      ? augmentKeyPosesWithIntervalKeys({
          userKeyPoses: selectUserKeyPoseApiPayloadForReference(
            this.state,
            referenceName,
          ),
          existingIntervalKeyPoses: selectIntervalStampApiPayloadForReference(
            this.state,
            referenceName,
          ),
          ...intervalAugmentation,
        })
      : keyPoses;

    const { data, error } = await putSceneObjectMotionKeyPoses(
      sceneID,
      referenceName,
      timelineIndex,
      { keyPoses: keyPosesToSave },
      apiKey,
      signal,
    );

    if (error) {
      this.state.persistenceByMotionKey[persistenceKey] = {
        status: 'failed',
        error: getErrorMessage(error, 'Failed to save key poses.'),
      };
      return false;
    }

    const response = isSaveKeyPosesResponse(data) ? data : null;
    this.state.persistenceByMotionKey[persistenceKey] = {
      status: 'saved',
      error: null,
      metadata: response?.metadata,
      revision: response?.revision,
      savedKeyPoses: response?.keyPoses ?? keyPoses,
      updatedAt: response?.updatedAt,
      version: response?.version,
    };

    return true;
  }

  async saveCurrentSceneObjectMotionKeyPoses({
    sceneID,
    referenceName,
    timelineIndex,
    intervalAugmentation,
    signal,
  }: SaveCurrentSceneObjectMotionKeyPosesInput): Promise<boolean> {
    return this.saveSceneObjectMotionKeyPoses({
      sceneID,
      referenceName,
      timelineIndex,
      keyPoses: selectKeyPoseApiPayloadForReference(this.state, referenceName),
      intervalAugmentation,
      signal,
    });
  }

  async loadSceneObjectMotionEditHistory(
    input: Omit<LoadSceneObjectMotionEditHistoryInput, 'apiKey'>,
  ) {
    const apiKey = this.apiKey;
    if (!apiKey) {
      this.cancelGenerationHistoryLoad();
      this.state.generationHistory = [];
      this.syncComparisonGhostSelection();
      return false;
    }

    const requestID = this.startGenerationHistoryLoad();
    try {
      const result = await loadSceneObjectMotionEditHistory(
        this.getGenerationActionDeps(),
        {
          ...input,
          apiKey,
          signal: this.generationHistoryLoadAbortController?.signal,
          shouldApplyResult: () =>
            this.isCurrentGenerationHistoryLoad(requestID),
        },
      );

      if (this.isCurrentGenerationHistoryLoad(requestID)) {
        this.syncComparisonGhostSelection();
      }

      return result;
    } finally {
      this.finishGenerationHistoryLoad(requestID);
    }
  }

  clearGenerationHistory() {
    this.cancelGenerationHistoryLoad();
    clearGenerationHistory(this.getGenerationActionDeps());
    this.syncComparisonGhostSelection();
  }

  async regenerateSceneObjectMotionKeyPoses(
    input: Omit<RegenerateSceneObjectMotionKeyPosesInput, 'apiKey'>,
  ): Promise<RegenerateSceneObjectMotionKeyPosesResult> {
    const apiKey = this.apiKey;
    if (!apiKey) {
      return { ok: false, error: AUTH_ERROR_MESSAGE };
    }

    const result = await regenerateSceneObjectMotionKeyPoses(
      this.getGenerationActionDeps(),
      { ...input, apiKey },
    );
    if (result.ok) {
      this.state.comparisonGhost.appliedHistoryJobID = result.job.jobID;
      this.clearComparisonGhostHistoryEntry();
      this.clearHoverPreview();
      this.hideKeyPosesForReference({ referenceName: input.referenceName });
    } else {
      this.syncComparisonGhostSelection();
    }
    return result;
  }

  async recoverSceneObjectMotionKeyPoseGeneration(
    input: Omit<RecoverSceneObjectMotionKeyPoseGenerationInput, 'apiKey'>,
  ): Promise<KeyPoseGenerationActionResult> {
    const apiKey = this.apiKey;
    if (!apiKey) {
      return { ok: false, error: AUTH_ERROR_MESSAGE };
    }

    const result = await recoverSceneObjectMotionKeyPoseGeneration(
      this.getGenerationActionDeps(),
      { ...input, apiKey },
    );
    this.syncComparisonGhostSelection();
    return result;
  }

  async applyHistoryGenerationToScene(
    input: ApplyHistoryGenerationToSceneStoreInput,
  ): Promise<KeyPoseGenerationActionResult> {
    const { createRecoveredKeyPoses, ...applyInput } = input;
    const apiKey = this.apiKey;
    if (!apiKey) {
      return { ok: false, error: AUTH_ERROR_MESSAGE };
    }

    const loadKeyPoses = this.supportsKeyPoseEditingForReference(
      applyInput.entry.referenceName,
    );
    const result = await applyHistoryGenerationToScene(
      this.getGenerationActionDeps(),
      { ...applyInput, apiKey, loadKeyPoses },
    );
    if (result.ok && loadKeyPoses) {
      const restoredKeyPoses = createRecoveredKeyPoses
        ? createRecoveredKeyPoses(result.keyPoses)
        : createRecoveredKeyPoseInputsFromApiKeyPoses({
            keyPoses: result.keyPoses,
          });

      this.replaceRecoveredSceneKeyPoses({
        referenceName: applyInput.entry.referenceName,
        keyPoses: restoredKeyPoses,
      });
      this.state.comparisonGhost.appliedHistoryJobID = applyInput.entry.jobID;
      if (
        this.state.comparisonGhost.selectedHistoryJobID ===
        applyInput.entry.jobID
      ) {
        this.clearComparisonGhostHistoryEntry();
      }

      const didSaveRestoredKeyPoses = await this.saveSceneObjectMotionKeyPoses({
        sceneID: applyInput.entry.sceneID,
        referenceName: applyInput.entry.referenceName,
        timelineIndex: applyInput.entry.timelineIndex,
        keyPoses: result.keyPoses,
        signal: applyInput.signal,
      });
      if (!didSaveRestoredKeyPoses) {
        this.syncComparisonGhostSelection();
        return {
          ok: false,
          error:
            'Motion applied, but restored key poses could not be saved. Refresh may restore the previous key poses.',
        };
      }
    } else if (result.ok) {
      this.state.comparisonGhost.appliedHistoryJobID = applyInput.entry.jobID;
      if (
        this.state.comparisonGhost.selectedHistoryJobID ===
        applyInput.entry.jobID
      ) {
        this.clearComparisonGhostHistoryEntry();
      }
    }
    this.syncComparisonGhostSelection();
    return result.ok
      ? { ok: true, job: result.job }
      : { ok: false, error: result.error };
  }

  async getHistoryGenerationBvhURL(
    input: Omit<GetHistoryGenerationBvhURLInput, 'apiKey'>,
  ): Promise<{ ok: true; url: string } | { ok: false; error: string }> {
    const apiKey = this.apiKey;
    if (!apiKey) {
      return { ok: false, error: AUTH_ERROR_MESSAGE };
    }

    const result = await getHistoryGenerationBvhURL(
      this.getGenerationActionDeps(),
      { ...input, apiKey },
    );
    this.syncComparisonGhostSelection();
    return result;
  }

  async selectComparisonGhostHistoryEntry({
    entry,
    scope: inputScope,
  }: SelectComparisonGhostHistoryEntryInput): Promise<
    { ok: true } | { ok: false; error: string }
  > {
    const scope = inputScope ?? this.getComparisonGhostScope();
    const scopeKey = getKeyPoseComparisonGhostScopeKey(scope);
    const playableEntries = selectPlayableKeyPoseComparisonGhostHistoryEntries(
      this.state.generationHistory,
      scope,
    );
    const availableEntryCount =
      selectAvailableKeyPoseComparisonGhostHistoryEntries(
        this.state.generationHistory,
        scope,
      ).length;
    const selectedEntry = playableEntries.find(
      (historyEntry) => historyEntry.jobID === entry.jobID,
    );

    if (!selectedEntry || availableEntryCount <= 1) {
      return {
        ok: false,
        error: 'This motion is not available for ghost comparison.',
      };
    }

    const result = await this.refreshComparisonGhostBvhURL(selectedEntry);
    if (!result.ok) {
      return result;
    }

    if (scopeKey && this.state.comparisonGhost.scopeKey !== scopeKey) {
      this.state.comparisonGhost = {
        ...createInitialKeyPoseComparisonGhostState(),
        scopeKey,
      };
    }
    this.state.comparisonGhost.selectedHistoryJobID = selectedEntry.jobID;
    this.state.comparisonGhost.disabled = false;
    this.state.comparisonGhost.error = null;
    return { ok: true };
  }

  clearComparisonGhostHistoryEntry() {
    this.state.comparisonGhost.disabled = true;
    this.state.comparisonGhost.selectedHistoryJobID = null;
    this.state.comparisonGhost.loadingHistoryJobID = null;
    this.state.comparisonGhost.error = null;
  }

  enableDefaultComparisonGhostHistoryEntry() {
    this.state.comparisonGhost.disabled = false;
    this.state.comparisonGhost.selectedHistoryJobID = null;
    this.state.comparisonGhost.error = null;
    this.syncComparisonGhostSelection();
  }

  private async refreshComparisonGhostBvhURL(
    entry: KeyPoseGenerationHistoryEntry,
  ): Promise<{ ok: true; url: string } | { ok: false; error: string }> {
    const apiKey = this.apiKey;
    if (!apiKey) {
      return { ok: false, error: AUTH_ERROR_MESSAGE };
    }

    this.state.comparisonGhost.loadingHistoryJobID = entry.jobID;
    this.state.comparisonGhost.error = null;

    const result = await getHistoryGenerationBvhURL(
      this.getGenerationActionDeps(),
      { entry, apiKey },
    );

    if (this.state.comparisonGhost.loadingHistoryJobID === entry.jobID) {
      this.state.comparisonGhost.loadingHistoryJobID = null;
      this.state.comparisonGhost.error = result.ok ? null : result.error;
    }

    this.syncComparisonGhostSelection();
    return result;
  }

  private supportsKeyPoseEditingForReference(referenceName: string) {
    const actor = this.deps.parentStore.state.playback.sceneActors.find(
      (sceneActor) => sceneActor.referenceName === referenceName,
    );
    if (!actor) {
      return false;
    }

    return isBuiltinFreeCharacterId(actor.characterID);
  }

  private get apiKey() {
    return this.deps.global.userStore.state.apiKey.data?.apiKey ?? null;
  }

  private getGenerationActionDeps(): KeyPoseGenerationActionDeps {
    return {
      onGenerationHistoryChanged: () => this.syncComparisonGhostSelection(),
      parentStore: this.deps.parentStore,
      state: this.state,
    };
  }

  private syncApiKeyAvailability() {
    this.state.hasApiKey = Boolean(this.apiKey);
  }

  private syncSceneScope() {
    const sceneID = this.deps.parentStore.state.scene.data?.sceneID ?? null;
    if (this.sceneID === sceneID) {
      return;
    }

    this.cancelGenerationHistoryLoad();
    this.sceneID = sceneID;
    this.state.generationHistory = [];
    this.state.clipboard = null;
    this.state.hoverPreview = null;
    this.state.editing = createInitialKeyPoseEditingState();
    this.state.actionStack = createInitialKeyPoseActionStackState();
    this.state.comparisonGhost = createInitialKeyPoseComparisonGhostState();
    this.state.unsupportedCharacterKeyPosePrompt = null;
    this.state.referencesByName = {};
    this.state.keyPosesById = {};
    this.state.persistenceByMotionKey = {};
    this.syncActiveActor();
    this.state.generationHistoryLoading =
      this.canLoadGenerationHistoryForCurrentScope();
    this.syncComparisonGhostSelection();
  }

  private syncActiveActor() {
    const { selectedReferenceName } = this.deps.parentStore.state.playback;
    if (this.state.activeReferenceName === selectedReferenceName) {
      return;
    }

    const previousReferenceName = this.state.activeReferenceName;
    const shouldInvalidateGenerationHistory =
      selectedReferenceName === null ||
      (previousReferenceName !== null &&
        previousReferenceName !== selectedReferenceName);

    if (shouldInvalidateGenerationHistory) {
      this.cancelGenerationHistoryLoad();
      this.state.generationHistory = [];
    }

    this.state.activeReferenceName = selectedReferenceName;
    this.clearHoverPreview();
    this.clearKeyPoseEditingPreview();
    this.state.editing.selectedFkJointId = null;
    this.state.editing.hoveredFkJointId = null;
    this.state.editing.selectedIkEffectorId = null;
    this.state.editing.hoveredIkEffectorId = null;
    this.state.editing.lockFeet = false;
    this.state.editing.dragging = false;
    if (selectedReferenceName) {
      this.ensureReferenceState(selectedReferenceName);
    }
    this.state.generationHistoryLoading =
      this.canLoadGenerationHistoryForCurrentScope();
    this.syncComparisonGhostSelection();
  }

  private startGenerationHistoryLoad() {
    this.generationHistoryLoadAbortController?.abort();
    this.generationHistoryLoadRequestID += 1;
    this.generationHistoryLoadAbortController = new AbortController();
    this.state.generationHistory = [];
    this.state.generationHistoryLoading = true;
    this.syncComparisonGhostSelection();

    return this.generationHistoryLoadRequestID;
  }

  private finishGenerationHistoryLoad(requestID: number) {
    if (!this.isCurrentGenerationHistoryLoad(requestID)) {
      return;
    }

    this.generationHistoryLoadAbortController = null;
    this.state.generationHistoryLoading = false;
  }

  private cancelGenerationHistoryLoad() {
    this.generationHistoryLoadAbortController?.abort();
    this.generationHistoryLoadAbortController = null;
    this.generationHistoryLoadRequestID += 1;
    this.state.generationHistoryLoading = false;
  }

  private isCurrentGenerationHistoryLoad(requestID: number) {
    return this.generationHistoryLoadRequestID === requestID;
  }

  private canLoadGenerationHistoryForCurrentScope() {
    return Boolean(
      this.apiKey &&
        resolveKeyPoseMotionContext({
          scene: this.deps.parentStore.state.scene,
          playback: this.deps.parentStore.state.playback,
          keyPoseReferenceName: this.state.activeReferenceName,
        }),
    );
  }

  private syncComparisonGhostSelection() {
    const scope = this.getComparisonGhostScope();
    const scopeKey = getKeyPoseComparisonGhostScopeKey(scope);
    if (!scopeKey) {
      this.state.comparisonGhost = createInitialKeyPoseComparisonGhostState();
      return;
    }

    if (this.state.comparisonGhost.scopeKey !== scopeKey) {
      this.state.comparisonGhost = {
        ...createInitialKeyPoseComparisonGhostState(),
        scopeKey,
      };
    }

    if (this.state.comparisonGhost.disabled) {
      this.state.comparisonGhost.selectedHistoryJobID = null;
      this.state.comparisonGhost.loadingHistoryJobID = null;
      return;
    }

    const selectedEntry = selectKeyPoseComparisonGhostHistoryEntry(
      this.state.generationHistory,
      this.state.comparisonGhost.selectedHistoryJobID,
      scope,
    );
    if (!selectedEntry) {
      this.state.comparisonGhost.selectedHistoryJobID = null;
      this.state.comparisonGhost.loadingHistoryJobID = null;
      this.state.comparisonGhost.error = null;
      return;
    }

    this.state.comparisonGhost.selectedHistoryJobID = selectedEntry.jobID;

    if (
      !selectedEntry.outputBvhURL &&
      this.state.comparisonGhost.loadingHistoryJobID !== selectedEntry.jobID &&
      !this.state.comparisonGhost.error
    ) {
      void this.refreshComparisonGhostBvhURL(selectedEntry);
    }
  }

  private getComparisonGhostScope(): KeyPoseComparisonGhostScope | null {
    const context = resolveKeyPoseMotionContext({
      scene: this.deps.parentStore.state.scene,
      playback: this.deps.parentStore.state.playback,
      keyPoseReferenceName: this.state.activeReferenceName,
    });
    if (!context) {
      return null;
    }

    return {
      appliedMotionEditJobID: context.appliedMotionEditJobID,
      bvhUrls: context.bvhUrls,
      sceneID: context.sceneID,
      referenceName: context.referenceName,
      motionID: context.motionID,
      originalBvhURL: context.originalBvhURL,
      timelineIndex: context.timelineIndex,
    };
  }

  private ensureKeyPoseIkTargetPositions(
    keyPoseId: KeyPoseId,
  ): KeyPoseIkTargetPositions {
    if (!this.state.editing.ikTargetPositionsByKeyPoseId[keyPoseId]) {
      this.state.editing.ikTargetPositionsByKeyPoseId[keyPoseId] = {};
    }

    return this.state.editing.ikTargetPositionsByKeyPoseId[keyPoseId];
  }

  private clearDeletedKeyPoseEditingState(keyPoseId: KeyPoseId) {
    delete this.state.editing.previewByKeyPoseId[keyPoseId];
    delete this.state.editing.ikTargetPositionsByKeyPoseId[keyPoseId];
    if (this.state.editing.draggingKeyPoseId === keyPoseId) {
      this.setKeyPoseEditingDragging(false);
    }
  }

  private applyKeyPoseActionSnapshot({
    referenceName,
    keyPoseId,
    snapshot,
  }: {
    referenceName: string;
    keyPoseId: KeyPoseId;
    snapshot: KeyPoseActionSnapshot;
  }) {
    const nextSnapshot = cloneKeyPoseActionSnapshot(snapshot);
    this.removeKeyPoseIdFromReferences(keyPoseId);

    if (nextSnapshot.keyPose) {
      const nextKeyPose = {
        ...nextSnapshot.keyPose,
        id: keyPoseId,
      };
      this.state.keyPosesById[keyPoseId] = nextKeyPose;
      const referenceState = this.ensureReferenceState(referenceName);
      referenceState.keyPoseIds.push(keyPoseId);
      this.sortReferenceKeyPoseIds(referenceState);
    } else {
      delete this.state.keyPosesById[keyPoseId];
    }

    if (nextSnapshot.ikTargetPositions) {
      this.state.editing.ikTargetPositionsByKeyPoseId[keyPoseId] =
        nextSnapshot.ikTargetPositions;
    } else {
      delete this.state.editing.ikTargetPositionsByKeyPoseId[keyPoseId];
    }

    delete this.state.editing.previewByKeyPoseId[keyPoseId];
    if (this.state.hoverPreview?.referenceName === referenceName) {
      this.clearHoverPreview();
    }
    this.state.activeReferenceName = referenceName;
  }

  private removeKeyPoseFromReference(
    keyPoseId: KeyPoseId,
    referenceState: ReferenceKeyPoseState,
  ) {
    const keyPoseIndex = referenceState.keyPoseIds.indexOf(keyPoseId);
    if (keyPoseIndex !== -1) {
      referenceState.keyPoseIds.splice(keyPoseIndex, 1);
    }

    delete this.state.keyPosesById[keyPoseId];
    this.clearDeletedKeyPoseEditingState(keyPoseId);
  }

  private removeKeyPoseIdFromReferences(keyPoseId: KeyPoseId) {
    for (const referenceState of Object.values(this.state.referencesByName)) {
      const keyPoseIndex = referenceState.keyPoseIds.indexOf(keyPoseId);
      if (keyPoseIndex !== -1) {
        referenceState.keyPoseIds.splice(keyPoseIndex, 1);
      }
    }
  }

  private sortReferenceKeyPoseIds(referenceState: ReferenceKeyPoseState) {
    referenceState.keyPoseIds.sort((a, b) => {
      const leftFrame = this.state.keyPosesById[a]?.frame ?? Infinity;
      const rightFrame = this.state.keyPosesById[b]?.frame ?? Infinity;
      return leftFrame - rightFrame;
    });
  }

  private replaceState(nextState: KeyPoseState) {
    Object.assign(this.state, nextState);
  }

  private ensureReferenceState(referenceName: string): ReferenceKeyPoseState {
    if (!this.state.referencesByName[referenceName]) {
      this.state.referencesByName[referenceName] = {
        referenceName,
        keyPoseIds: [],
      };
    }

    return this.state.referencesByName[referenceName];
  }

  private createRecoveredKeyPoseRecord(
    referenceName: string,
    keyPose: ReplaceRecoveredSceneKeyPoseInput,
    index: number,
  ): KeyPoseRecord {
    const frame = Math.max(Math.round(keyPose.frame), 0);
    const keyPoseId =
      keyPose.id?.trim() || `${referenceName}:${frame}:${index}`;
    const stamp = resolveKeyPoseStamp({ id: keyPoseId });

    return {
      id: keyPoseId,
      frame,
      stamp,
      ...(stamp === 'interval' ? { hidden: true } : {}),
      skeletonSnapshot: cloneKeyPoseSkeletonSnapshot(
        keyPose.skeletonSnapshot,
        frame,
      ),
      sourcePose: cloneKeyPoseSourcePose(keyPose.sourcePose, frame),
    };
  }

  private findReferenceNameForKeyPose(keyPoseId: KeyPoseId) {
    return (
      Object.values(this.state.referencesByName).find((referenceState) =>
        referenceState.keyPoseIds.includes(keyPoseId),
      )?.referenceName ?? null
    );
  }

  private writeKeyPose(
    {
      frame,
      referenceName,
      skeletonSnapshot,
      sourcePose,
      stamp = 'user',
    }: AddKeyPoseInput,
    replaceExisting: boolean,
  ): KeyPoseWriteResult | null {
    const normalizedReferenceName = referenceName.trim();
    if (!normalizedReferenceName) {
      return null;
    }

    const normalizedFrame = Math.max(Math.round(frame), 0);
    const normalizedSnapshot = cloneKeyPoseSkeletonSnapshot(
      skeletonSnapshot,
      normalizedFrame,
    );
    const normalizedSourcePose = cloneKeyPoseSourcePose(
      sourcePose,
      normalizedFrame,
    );
    const referenceState = this.ensureReferenceState(normalizedReferenceName);
    const existingKeyPoseId = referenceState.keyPoseIds.find(
      (keyPoseId) =>
        this.state.keyPosesById[keyPoseId]?.frame === normalizedFrame,
    );
    const existingKeyPose = existingKeyPoseId
      ? this.state.keyPosesById[existingKeyPoseId]
      : null;

    if (existingKeyPoseId && existingKeyPose) {
      if (shouldReplaceIntervalStampWithUserKeyPose(stamp, existingKeyPose)) {
        this.removeKeyPoseFromReference(existingKeyPoseId, referenceState);
        console.log(
          `[keyPoseIntervalAugmentation] interval key pose upgraded to user at ${normalizedFrame} frame`,
        );
      } else if (replaceExisting) {
        existingKeyPose.skeletonSnapshot = normalizedSnapshot;
        existingKeyPose.sourcePose = normalizedSourcePose;
        existingKeyPose.stamp = stamp;
        if (stamp === 'interval') {
          existingKeyPose.hidden = true;
        } else {
          delete existingKeyPose.hidden;
        }
        this.state.activeReferenceName = normalizedReferenceName;
        return {
          keyPoseId: existingKeyPoseId,
          created: false,
          didWrite: true,
          frame: normalizedFrame,
          referenceName: normalizedReferenceName,
        };
      } else {
        this.state.activeReferenceName = normalizedReferenceName;
        return {
          keyPoseId: existingKeyPoseId,
          created: false,
          didWrite: false,
          frame: normalizedFrame,
          referenceName: normalizedReferenceName,
        };
      }
      this.state.activeReferenceName = normalizedReferenceName;
      return {
        keyPoseId: existingKeyPoseId,
        created: false,
        didWrite: replaceExisting,
        frame: normalizedFrame,
        referenceName: normalizedReferenceName,
      };
    }

    const keyPoseId =
      stamp === 'interval'
        ? createIntervalKeyPoseId(normalizedFrame)
        : crypto.randomUUID();
    this.state.keyPosesById[keyPoseId] = {
      id: keyPoseId,
      frame: normalizedFrame,
      stamp,
      ...(stamp === 'interval' ? { hidden: true } : {}),
      skeletonSnapshot: normalizedSnapshot,
      sourcePose: normalizedSourcePose,
    };
    referenceState.keyPoseIds.push(keyPoseId);
    referenceState.keyPoseIds.sort(
      (a, b) =>
        this.state.keyPosesById[a].frame - this.state.keyPosesById[b].frame,
    );
    this.state.activeReferenceName = normalizedReferenceName;

    return {
      keyPoseId,
      created: true,
      didWrite: true,
      frame: normalizedFrame,
      referenceName: normalizedReferenceName,
    };
  }
}

function isSaveKeyPosesResponse(
  value: SaveKeyPosesResponse | undefined,
): value is SaveKeyPosesResponse {
  return Boolean(value && typeof value === 'object');
}

function countKeyPoseIkTargetPositions(
  targetPositions: KeyPoseIkTargetPositions | null,
) {
  if (!targetPositions) {
    return 0;
  }

  return Object.values(targetPositions).filter(Boolean).length;
}

export const { Provider, useStore, useStoreApi, Context } = createStoreProvider(
  {
    Store: KeyPoseStore,
    displayName: 'KeyPoseStore',
    parentStores: { parentStore: MotionEditorStoreContext },
    staticDeps: { global: globalStores },
  },
);
